import { ColumnSettings } from 'src/app/core/model/column-settings';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { AppointmentService } from './../../core/service/appointment.service';
import { UserService } from 'src/app/core/service/user.service';
import { SettingEdit } from './../model/settingEdit';
import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, ViewChild } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { TeamService } from 'src/app/core/service/team.service';
import { TeamRole } from 'src/app/team/model/team';
import { SettingList } from '../model/settingList';
import { ProviderService } from 'src/app/core/service/provider.service';
import { LanguageService } from 'src/app/core/service/language.service';
import { AssessmentListService } from 'src/app/core/service/grid-data/grid-data-assessment.service';
import { MemberService } from 'src/app/core/service/member.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { ChannelService } from 'src/app/core/service/channel.service';
import { EmailTemplateList } from 'src/app/email-template/model/email-template';
import { EmailTemplateService } from 'src/app/core/service/email-template.service';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { forkJoin } from 'rxjs';
import { sortBy } from 'lodash';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { ConsentService } from 'src/app/core/service/consent.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { process } from "@progress/kendo-data-query";
import { AssessmentService } from 'src/app/core/service/assessment.service';

@Component({
  standalone: false,
  selector: 'app-setting-list',
  templateUrl: './setting-list.component.html',
  styleUrls: ['./setting-list.component.scss']
})
export class SettingListComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public rolesView: GridViewManager;
  public enableAdvanced = false;
  public count: number;
  public editDataItem: SettingEdit;
  public filterText = '';
  public isPerOrganization = false;
  public programLeadsUsers: any[] = [];
  public allUsers: any[] = [];
  public teamRoles: TeamRole[] = [];
  public patientLabelString: string;
  public assessments: any[] = [];
  public channels: any[];
  public subjectIdentifiers: Array<{ displayName: string; id: string }>;
  public resourceStatuses: Array<{ displayName: string; id: string }>;
  public codeSets: Array<{ displayName: string; id: string }>;
  public sendInviteTemplates: EmailTemplateList[] = [];
  public rolesColumnsConfig: ColumnSettings[];
  public roles: Array<{ text: string; value: string }>;
  public rolesUpdated = false;
  public loaded = false;
  public rolesTitleSelection = [];
  public consentTypes: Array<{ displayName: string; code: string; id: string }>;
  public communityServiceIdentifierTypes: Array<{ displayName: string; id: string }>;

  /**
   * Setting keys with the corresponding translations.
   */
  public settingKeyTexts: Array<{ key: string; text: string }>;

  private textYES = '';
  private textNO = '';
  private textGridFilterValue = '';
  private enabledViews: string;
  private communityEnabled: boolean;
  private appointmentsEnabled: boolean;
  private attachmentsEnabled: boolean;
  private insurancesEnabled: boolean;
  private contactsEnabled: boolean;
  private sendEmailTemplateLogo: boolean;
  private goalDictionary = '';

  /**
   * Constructor
   */
  constructor(
    private settingService: SettingService,
    private assessmentService: AssessmentService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private translate: TranslateService,
    private userService: UserService,
    private teamService: TeamService,
    private appointmentService: AppointmentService,
    private providerService: ProviderService,
    private languageService: LanguageService,
    private assessmentListService: AssessmentListService,
    private memberService: MemberService,
    private dictionaryService: DictionaryService,
    private emailTemplateService: EmailTemplateService,
    private messengerService: MessengerService,
    private channelService: ChannelService,
    private consentService: ConsentService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  public ngOnInit(): void
  {
    this.roles = this.userService.roles.filter(x => x.value !== 'ROLE_SYSTEM_OPERATOR' && x.value !== 'ROLE_COMMUNITY_USER' &&
      x.value !== 'ROLE_COMMUNITY_ADMIN');

    this.view.startLoading();
    this.assessmentService.getAllAssessmentMinimal().subscribe({
      next: (assessments) =>
      {
        this.assessments = assessments;
        this.channelService.getCacheMinimalChannels().subscribe({
          next: (channels) =>
          {
            this.channels = channels;

            this.settingService.getCachedSessionSettings().subscribe({
              next: (sessionSetting) =>
              {
                this.setPublicSettings(sessionSetting);

                this.translate.get(['YES', 'NO', 'DEFAULT-GRID-FILTER-VALUE']).subscribe({
                  next: (res) =>
                  {
                    this.textYES = res.YES;
                    this.textNO = res.NO;
                    this.textGridFilterValue = res['DEFAULT-GRID-FILTER-VALUE'];
                  }
                });

                // Get all the setting keys to dynamically load proper translations
                this.languageService.getLanguageAssets('en.json').subscribe({
                  next: (languages: []) =>
                  {
                    const translationKeys = [];
                    for (const key in languages) { if (key.includes('SETTING-KEY-')) { translationKeys.push(key); } }
                    this.translate.get(translationKeys).subscribe({
                      next: (res) =>
                      {
                        this.settingKeyTexts = [];
                        for (const key in res)
                        {
                          if ({}.hasOwnProperty.call(res, key))
                          {
                            // Name of translation keys matches the setting key after removing prefix and replace - by <blank>
                            const settingKey = key.replace('SETTING-KEY-', '').replace(/-/g, '');
                            if (res[key].includes('{{patientLabel}}'))
                            {
                              this.settingKeyTexts.push({ key: settingKey, text: res[key].replace(/{{patientLabel}}/g, this.patientLabelString) });
                            }
                            else
                            {
                              this.settingKeyTexts.push({ key: settingKey, text: res[key] });
                            }
                          }
                        }
                        this.teamService.getCachedTeamRoles().subscribe({
                          next: (data) =>
                          {
                            this.teamRoles = data;
                            this.userService.getCachedUsers().subscribe({
                              next: (allUsers: any) =>
                              {
                                this.programLeadsUsers = allUsers.filter(x => x.role === 'ROLE_PROGRAM_LEAD');
                                this.allUsers = allUsers;
                                this.loadSettings();
                              },
                              error: (errorResponse: any) =>
                              {
                                this.messageHandlerService.displayError(errorResponse);
                              }
                            });
                          }
                        });
                      }
                    });
                  }
                });
              }
            });

            this.dictionaryService.getCachedIdentifiers().subscribe({
              next: (data) =>
              {
                this.subjectIdentifiers = data.map(d => ({ id: d.id.toString(), displayName: d.displayName }));
              }
            });

            this.dictionaryService.getResourceStatuses().subscribe({
              next: (data) =>
              {
                this.resourceStatuses = data.map(d => ({ id: d.id.toString(), displayName: d.displayName }));
              }
            });

            this.dictionaryService.getCachedCommunityServiceIdentifierTypes().subscribe({
              next: (data) =>
              {
                this.communityServiceIdentifierTypes = data.map(d => ({ id: d.id.toString(), displayName: d.displayName }));;
              }
            });

            this.dictionaryService.getCachedMinimalCodeSets().subscribe({
              next: (data) =>
              {
                this.codeSets = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.description })), ['displayName']);
              },
              error: (error) =>
              {
                this.messageHandlerService.displayError(error);
              }
            });

            this.emailTemplateService.getCachedEmailTemplatesList('CommunityInvite').subscribe({
              next: (data) =>
              {
                this.sendInviteTemplates = data;
              },
              error: errorResponse =>
              {
                this.messageHandlerService.displayError(errorResponse);
              }
            });

            this.dictionaryService.getCachedConsentTypes().subscribe({
              next: (data) =>
              {
                this.consentTypes = sortBy(data.map(d => ({ id: d.id.toString(), code: d.code, displayName: d.displayName })), ['displayName']);
              }
            });
          }
        });
      }
    });
  }

  /**
   * Convert a list of Assessments to assessment texts.
   *
   * @param assessments List of assessments.
   */
  public getAssessmentDisplayTexts(assessments: string): string
  {
    const res = assessments.split(',');
    const assessmentsText = [];
    res.forEach(assessment =>
    {
      const assessmentText = this.getAssessmentText(assessment);
      if (assessmentText !== '')
      {
        assessmentsText.push(assessmentText);
      }
    });
    return assessmentsText.join();
  }

  /**
   * Get the Assessment text to display.
   *
   * @param assessment Assessment string.
   */
  public getAssessmentText(assessment: string): string
  {
    let text = assessment;
    const assessmentObject = this.assessments.find(x => x.id === parseInt(assessment, 10));
    if (assessmentObject !== undefined)
    {
      text = assessmentObject.name;
    }
    return text;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public rolesDataStateChange(state: DataStateChangeEvent): void
  {
    this.rolesView.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Grid row editing event handler.
   *
   * @param id Setting id
   */
  public editHandler(id: number): void
  {
    this.settingService.getSettingById(id).subscribe({
      next: (data) =>
      {
        data.name = this.getSettingName(data.key);
        this.isPerOrganization = this.isPerOrganizationSetting(data.key);
        setTimeout(() =>
        {
          this.editDataItem = data;
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public getRecordContactFields(setting: any): string
  {
    const result = [];
    setting.recordContactFields.forEach(field =>
    {
      if (field.valueVisible === true)
      {
        const value = this.memberService.recordContact.find(x => x.text === field.name).value + '|' + (field.valueRequired === null || field.valueRequired === false ? 'false' : 'true');
        result.push(value);
      }
    });
    return result.join();
  }

  public convertConsentExpirationPolicyToSave(setting: any): string
  {
    const result = [];
    const defaultValue = this.translate.instant('DEFAULT').charAt(0) + '|' + setting.value + '|' + setting.consentExpirationPolicyUnit.charAt(0) + (setting.consentExpirationPolicies.size > 0 ? '|' : ''); // default
    result.push(defaultValue);
    setting.consentExpirationPolicies.forEach(consent =>
    {
      if (consent.consentType !== null && consent.consentExpirationPolicyValue !== null && consent.unit !== null)
      {
        const value = consent.consentType.code + '|' + consent.consentExpirationPolicyValue + '|' + consent.unit.charAt(0);
        result.push(value);
      }
    });
    return result.join();
  }

  /**
   * Grid row editing event handler.
   *
   * @param setting Setting object.
   */
  public saveChangesHandler(setting: any): void
  {
    delete setting.internalValue;
    if (setting.key === 'LinkTokenExpirationMinutes')
    {
      setting.value = setting.value + setting.captureLinkTokenExpirationUnit.charAt(0);
    }
    if (setting.key === 'ConsentExpiration')
    {
      setting.value = this.convertConsentExpirationPolicyToSave(setting);
    }
    if (setting.key === 'RecordContactFields')
    {
      setting.value = this.getRecordContactFields(setting);
    }
    this.settingService.saveSetting(setting, setting.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        data.name = this.getSettingName(data.key);
        data.organizations = setting.organizations;
        data.perOrganization = this.isPerOrganizationSetting(setting.key);
        data.internalValue = data.value;
        data.value = this.setDataValue(data);
        this.view.addOrUpdateGridRow(data);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('SETTING-SAVED-SUCCESSFULLY');

        if (data.key === 'HeaderLogoDocumentId')
        {
          const photoMessagingSubject = new MessageSubject('headerLogoChanged', { headerLogoDocumentId: data.internalValue });
          this.messengerService.sendMessage(photoMessagingSubject);
        }
        else
          if (data.key === 'AnalyticsRoles' || data.key === 'CommunityServiceSearchRoles' || data.key === 'ProvidersRoles' ||
            data.key === 'MessagesRoles' || data.key === 'ChatRoles' ||
            data.key === 'AllowOrganizationUsersRoles' || data.key === 'AllowOrganizationGroupsRoles')
          {
            this.messengerService.sendMessage(new MessageSubject('reloadSideMenu'));
          }

        // Must refresh enabled views parameters on save
        this.settingService.getCachedSessionSettings().subscribe({
          next: (sessionSetting) =>
          {
            this.setPublicSettings(sessionSetting);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel handler.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Apply filtering to the grid.
   *
   * @param filter Filter content.
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  /**
   * Filter text changed handler.
   *
   * @param filterText Filter entered.
   */
  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public editVisible(dataItem: any): boolean
  {
    switch (dataItem.key.toUpperCase())
    {
      case 'CONSENTINVITETOKENEXPIRATIONDAYS':
      case 'COMMUNITYPROVIDERMAXIMUMDISTANCE':
      case 'COMMUNITYASSESSMENTIDS':
      case 'SENDINVITEDEFAULTEMAILTEMPLATE':
      case 'SENDINVITEDEFAULTVALIDDAYS':
      case 'COMMUNITYPORTALENABLED':
      case 'REQUIREDCOMMUNITYRESOURCES':
      case 'SEARCHABLECOMMUNITYRESOURCES':
      case 'COMMUNITYSERVICESEARCHROLES':
      case 'COMMUNITYLABEL':
      case 'COMMUNITYMAPLATITUDELONGITUDE':
      case 'ADDUPDATECOMMUNITYRESOURCESROLES':
        return this.communityEnabled;
      case 'SCHEDULERAGENDADAYS':
        return this.appointmentsEnabled && this.enabledViews.includes('AGENDA');
      case 'SCHEDULERMULTIDAYS':
        return this.appointmentsEnabled && this.enabledViews.includes('MULTI_DAY');
      case 'SCHEDULERSTARTTIME':
      case 'SCHEDULERENDTIME':
        return this.appointmentsEnabled &&
          (this.enabledViews.includes('DAY') ||
            this.enabledViews.includes('MULTI_DAY') ||
            this.enabledViews.includes('WEEK') ||
            this.enabledViews.includes('WORK_WEEK') ||
            this.enabledViews.includes('WEEK_TIMELINE'));
      case 'SCHEDULERVIEWS':
      case 'SCHEDULERAPPOINTMENTFOLLOWUP':
      case 'SCHEDULERAPPOINTMENTREMINDER':
      case 'APPOINTMENTSCOUNTDAYS':
        return this.appointmentsEnabled;
      case 'ATTACHMENTSCOUNTDAYS':
        return this.attachmentsEnabled;
      case 'INSURANCESCOUNTDAYS':
        return this.insurancesEnabled;
      case 'CONTACTSHISTORYCOUNTDAYS':
      case 'RECORDCONTACTFIELDS':
        return this.contactsEnabled;
      case 'TEMPLATELOGODOCUMENTID':
        return this.sendEmailTemplateLogo;
      case 'ALLOWFREETEXTFORNEWGOALS':
        return this.goalDictionary !== '';
      default:
        return true;
    }
  }

  /**
   * Get the Language text to display.
   *
   * @param language Language value.
   */
  public getLanguageText(language: string): string
  {
    let text = language;
    const languageObject = this.languageService.languages.find(x => x.value === language);
    if (languageObject !== undefined)
    {
      text = languageObject.text;
    }
    return text;
  }

  public getLinkTokenExpiration(value: any): any
  {
    if (value !== null)
    {
      const intValue = value.slice(0, -1);
      const unit = value.charAt(value.length - 1);
      let unitName = '';
      switch (unit)
      {
        case 'M':
          unitName = this.translate.instant('CAPTURE-LINK-TOKEN-EXPIRATION-MINUTES');
          break;
        case 'D':
          unitName = this.translate.instant('CAPTURE-LINK-TOKEN-EXPIRATION-DAYS');
          break;
      }
      return intValue.concat(" ").concat(unitName.toLowerCase());
    }
  }

  public convertConsentExpirationPolicyToDisplayTexts(consentExpirationPolicy: any): string
  {
    const res = consentExpirationPolicy.split(',');

    res.forEach(consent =>
    {
      const consentType = consent.split('|')[0];
      let consentTypeText = '';
      if (consentType === this.translate.instant('DEFAULT').charAt(0))
      {
        consentTypeText = this.translate.instant('DEFAULT');
      }
      else
      {
        const consentTypeObject = this.consentTypes.find(x => x.code === consentType);
        if (consentTypeObject !== undefined)
        {
          consentTypeText = consentTypeObject.displayName;
        }
      }
      const unit = consent.split('|')[2];
      let unitName = '';
      switch (unit)
      {
        case this.translate.instant('CONSENT-EXPIRATION-POLICY-MONTHS').charAt(0):
          unitName = this.translate.instant('CONSENT-EXPIRATION-POLICY-MONTHS');
          break;
        case this.translate.instant('CONSENT-EXPIRATION-POLICY-DAYS').charAt(0):
          unitName = this.translate.instant('CONSENT-EXPIRATION-POLICY-DAYS');
          break;
      }
      consentExpirationPolicy = consentExpirationPolicy.replace(consentType + '|', consentTypeText + ' - ');
      consentExpirationPolicy = consentExpirationPolicy.replace('|' + unit, ' ' + unitName.toLowerCase());
    });
    return consentExpirationPolicy.replaceAll(',', ', ');
  }

  public onTabSelect(e: SelectEvent): void
  {
    if (e.index === 1)
    {
      this.buildRolesAccessGrid();
    }
  }

  /**
   * Cancel Access Roles changes event. Returns to first tab.
   */
  public onCancelRolesChanges(): void
  {
    this.tabstrip.selectTab(0);
  }

  /**
   * Save Access Roles changes.
   */
  public onSaveRolesChanges(): void
  {
    if (this.rolesUpdated)
    {
      let sendMessage = false;
      // Detect system settings that have been changed
      const saveQueries: any = [];
      this.rolesView.gridData.data.forEach(d =>
      {
        // Get roles that were selected
        const roles = [];
        Object.entries(d.roles).forEach(([key, value]) => { if (value) { roles.push(key); } });

        // Compare existing roles vs updated roles by comparison as string. If changed, store to saveQueries
        const currentRoles = d.internalValue.split(',').sort().join();
        const updatedRoles = roles.sort().join();
        if (updatedRoles !== currentRoles)
        {
          d.value = updatedRoles;
          delete d.internalValue;
          saveQueries.push(this.settingService.saveSetting(d, d.id));
          if (d.key === 'AnalyticsRoles' || d.key === 'CommunityServiceSearchRoles' || d.key === 'ProvidersRoles' ||
            d.key === 'MessagesRoles' || d.key === 'ChatRoles' ||
            d.key === 'AllowOrganizationUsersRoles' || d.key === 'AllowOrganizationGroupsRoles')
          {
            sendMessage = true;
          }
        }
      });

      // Perform system settings updates
      if (saveQueries.length > 0)
      {
        forkJoin(saveQueries).subscribe({
          complete: () =>
          {
            this.messageHandlerService.displaySuccess('ROLES-ACCESS-SAVED');
            this.loadSettings();
            if (sendMessage)
            {
              this.messengerService.sendMessage(new MessageSubject('reloadSideMenu'));

            }
          },
          error: (error) =>
          {
            // failed to retrieve channel, reset the view model and render with the error set for display
            this.messageHandlerService.displayError(error);
          },
        });
      }
      else
      {
        this.tabstrip.selectTab(0);
      }
    }
  }

  public isValidSettingForRole(key: string, role: string): boolean
  {
    return this.settingService.isValidSettingForRole(key, role);
  }

  public changeRoles(field: string): void
  {
    this.rolesView.data.forEach((d) =>
    {
      if (this.isValidSettingForRole(d.key, field) && d.roles[field] !== this.rolesTitleSelection[field])
      {
        d.roles[field] = this.rolesTitleSelection[field];
        this.rolesUpdated = true;
      }
    });
  }

  public allData(): ExcelExportData
  {
    this.rolesView.data.forEach(d =>
    {
      d.ROLE_CARE_COORDINATOR = this.isValidSettingForRole(d.key, 'ROLE_CARE_COORDINATOR') ? (d.roles.ROLE_CARE_COORDINATOR == true ? this.textYES : this.textNO) : '-';
      d.ROLE_PROGRAM_LEAD = this.isValidSettingForRole(d.key, 'ROLE_PROGRAM_LEAD') ? (d.roles.ROLE_PROGRAM_LEAD == true ? this.textYES : this.textNO) : '-';
      d.ROLE_OPERATOR = this.isValidSettingForRole(d.key, 'ROLE_OPERATOR') ? (d.roles.ROLE_OPERATOR == true ? this.textYES : this.textNO) : '-';
      d.ROLE_COMMUNITY_COORDINATOR = this.isValidSettingForRole(d.key, 'ROLE_COMMUNITY_COORDINATOR') ? (d.roles.ROLE_COMMUNITY_COORDINATOR == true ? this.textYES : this.textNO) : '-';
      d.ROLE_REFERRAL_COORDINATOR = this.isValidSettingForRole(d.key, 'ROLE_REFERRAL_COORDINATOR') ? (d.roles.ROLE_REFERRAL_COORDINATOR == true ? this.textYES : this.textNO) : '-';
      d.ROLE_DIRECTORY_MANAGER = this.isValidSettingForRole(d.key, 'ROLE_DIRECTORY_MANAGER') ? (d.roles.ROLE_DIRECTORY_MANAGER == true ? this.textYES : this.textNO) : '-';
      d.ROLE_DATA_ANALYST = this.isValidSettingForRole(d.key, 'ROLE_DATA_ANALYST') ? (d.roles.ROLE_DATA_ANALYST == true ? this.textYES : this.textNO) : '-';
    });
    const currentTake = this.rolesView.state.take;
    this.rolesView.state.take = this.rolesView.gridData.total;
    const result: ExcelExportData = {
      data: process(this.rolesView.data, this.rolesView.state).data
    };
    this.rolesView.state.take = currentTake;
    return result;
  }

  private buildRolesAccessGrid(): void
  {
    this.rolesUpdated = false;
    this.rolesView = new GridViewManager(99999);
    this.rolesView.startLoading();
    this.translate.get(['NAME']).subscribe({
      next: (res) =>
      {
        this.rolesColumnsConfig = [{ field: 'name', title: res.NAME, titleKeys: ['NAME'], width: 100, sortable: true, hidden: false }];
        this.roles.forEach((r) =>
        {
          this.rolesColumnsConfig.push({
            field: r.value, title: r.text, titleKeys: null,
            width: r.text.length >= 20 ? 80 : (r.text === 'OPERATOR' ? 40 : 60), sortable: false, hidden: false, class: 'text-center'
          });
          this.rolesTitleSelection[r.value] = false;
        });

        const rolesData = this.view.data.filter(x => x.key.includes('Roles'));
        const data = sortBy(rolesData, ['name']);
        data.forEach(d =>
        {
          d.roles = [];
          this.roles.forEach(r =>
          {
            d.roles[r.value] = this.userService.isRoleInList(r.value, d.internalValue);
          });
        });
        this.rolesView.setData(data);
        this.rolesView.setSort([{ field: 'name', dir: 'asc' }]);
        this.allData = this.allData.bind(this);
        this.rolesView.stopLoading();
      }
    });
  }

  /**
   * This method is called on ngOnInit and onSave to set latest setting values. This control if the setting is enabled or disabled.
   *
   * @param sessionSetting Setting object
   */
  private setPublicSettings(sessionSetting: SessionSetting): void
  {
    this.patientLabelString = sessionSetting.patientLabel;
    this.enabledViews = sessionSetting.schedulerViews;
    this.communityEnabled = sessionSetting.communityEnabled;
    this.appointmentsEnabled = sessionSetting.appointmentsRoles !== '';
    this.attachmentsEnabled = sessionSetting.attachmentsRoles !== '';
    this.insurancesEnabled = sessionSetting.insurancesRoles !== '';
    this.contactsEnabled = sessionSetting.contactsRoles !== '';
    this.sendEmailTemplateLogo = sessionSetting.sendEmailTemplateLogo;
    this.goalDictionary = sessionSetting.goalDictionary;
  }

  /**
   * Initial Load of grid data.
   */
  private loadSettings(): void
  {
    this.loaded = false;
    this.tabstrip.selectTab(0);
    this.view.startLoading();
    this.settingService.getSettingsList().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.lastModifiedOn = new Date(d.lastModifiedOn);
          d.name = this.getSettingName(d.key);
          d.perOrganization = this.isPerOrganizationSetting(d.key);
          d.internalValue = d.value;
          d.value = this.setDataValue(d);
        });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
        this.loaded = true;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private setDataValue(data: SettingList): string
  {
    let value = data.value;
    switch (data.key.toUpperCase())
    {
      case 'KNO2ENABLED':
      case 'ONERECORDENABLED':
      case 'COMMUNITYENABLED':
      case 'COMMUNITYPORTALENABLED':
      case 'DISPLAYSETTINGSMENUCARD':
      case 'PATIENTIMPORTMATCHNAMESANDDOB':
      case 'SUBJECTADDRESSREQUIRED':
      case 'SENDEMAILTEMPLATELOGO':
      case 'CARETEAMACCOUNTREGISTRATIONENABLED':
      case 'PATIENTRESTRICTIONSENABLED':
      case 'ALLOWMULTIPLECHANNELSENROLLMENT':
      case 'ALLOWMULTIPLEDASHBOARDENROLLMENT':
      case 'PAGINGRESTRICTIONPATIENTENABLED':
      case 'PAGINGRESTRICTIONCASEENABLED':
      case 'PAGINGRESTRICTIONTASKENABLED':
      case 'ALLOWALTERNATIVETWOSTEPAUTHENABLED':
      case 'TWOSTEPVERIFICATIONENABLEDWHENCREATINGNEWUSER':
      case 'EHRSHOWASSESSMENTS':
      case 'SHOWTRIBALAFFILIATIONS':
      case 'SHOWTRIBALENROLLMENTS':
      case 'SHOWDEPENDENTS':
      case 'PATIENTDEMOGRAPHICSENABLEFAX':
      case 'PATIENTDEMOGRAPHICSENABLEPAGER':
      case 'PATIENTDEMOGRAPHICSENABLEWEBSITE':
      case 'AUTOFITGRIDCOLUMNSPATIENT':
      case 'AUTOFITGRIDCOLUMNSTASK':
      case 'AUTOFITGRIDCOLUMNSCASE':
      case 'ALLOWFREETEXTFORNEWGOALS':
      case 'SHOWGENDER':
      case 'SHOWADMINISTRATIVEGENDER':
        value = data.value === 'true' ? this.textYES : this.textNO;
        break;
      case 'REPORTROLES':
      case 'PATIENTEDITROLES':
      case 'WORKFLOWSTATUSEDITROLES':
      case 'SENSITIVITIESEDITROLES':
      case 'COMMUNITYSERVICESEARCHROLES':
      case 'CONTACTSROLES':
      case 'APPOINTMENTSROLES':
      case 'INSURANCESROLES':
      case 'ATTACHMENTSROLES':
      case 'MESSAGESROLES':
      case 'CHATROLES':
      case 'NOTESROLES':
      case 'EVENTSROLES':
      case 'PINNEDRESOURCESROLES':
      case 'WELLBEINGROLES':
      case 'OTHERCASESROLES':
      case 'REFERRALSROLES':
      case 'CASEHISTORYROLES':
      case 'CASEEXTENSIONSROLES':
      case 'CASECARETEAMSROLES':
      case 'PATIENTCARETEAMSROLES':
      case 'PROVIDERSROLES':
      case 'ANALYTICSROLES':
      case 'GOALSROLES':
      case 'AUDITMEMBERROLES':
      case 'ADDUPDATECOMMUNITYRESOURCESROLES':
      case 'ALLOWORGANIZATIONUSERSROLES':
      case 'ALLOWORGANIZATIONGROUPSROLES':
      case 'ADDUPDATECHANNELCATEGORIESROLES':
      case 'ADDUPDATEDICTIONARIESROLES':
      case 'ADDUPDATEEXTENSIONSROLES':
      case 'ADDUPDATEFIELDCONFIGURATIONSROLES':
      case 'ADDUPDATEMETRICSROLES':
      case 'ADDUPDATEPROGRAMCATEGORIESROLES':
      case 'ADDUPDATESERVICECATEGORIESROLES':
      case 'ADDUPDATETERMSANDCONDITIONSROLES':
      case 'ADDUPDATEASSESSMENTSROLES':
      case 'ADDUPDATEASSESSMENTTYPESROLES':
      case 'ADDUPDATEPROGRAMTEMPLATESROLES':
      case 'ADDUPDATEFACILITIESROLES':
      case 'ADDUPDATECLIENTIMPORTROLES':
      case 'ADDUPDATEPROVIDERSROLES':
      case 'ADDUPDATETEAMROLESROLES':
      case 'ADDUPDATEINSURANCESROLES':
      case 'DELETEASSESSMENTSROLES':
      case 'ADDCASEASSESSMENTSROLES':
      case 'ADDCLIENTASSESSMENTSROLES':
      case 'ADDCASEREFERRALSROLES':
      case 'ADDCLIENTREFERRALSROLES':
      case 'CONTACTSCONTACTEDONROLES':
        value = this.userService.convertRolesToDisplayTexts(data.value);
        break;
      case 'LINKTOKENEXPIRATIONMINUTES':
        value = this.getLinkTokenExpiration(data.value);
        break;
      case 'CONSENTEXPIRATION':
        value = this.convertConsentExpirationPolicyToDisplayTexts(data.value);
        break;
      case 'REQUIREDDEMOGRAPHICS':
      case 'RECOMMENDEDDEMOGRAPHICS':
        value = this.memberService.convertDemographicsToDisplayTexts(data.value);
        break;
      case 'RECORDCONTACTFIELDS':
        value = data.value !== '' ? this.memberService.convertRecordContactToDisplayTexts(data.value) : '';
        break;
      case 'LOGINLANGUAGES':
        value = this.languageService.convertLoginLanguagesToDisplayTexts(data.value); // this.getLanguageText(data.value);
        break;
      case 'SEARCHABLECOMMUNITYRESOURCES':
      case 'REQUIREDCOMMUNITYRESOURCES':
        {
          value = this.memberService.convertCommunityResourcesToDisplayTexts(data.value);
          const splitted = value.split(',');
          for (let i = 0; i < splitted.length; i++)
          {
            if (splitted[i].includes('{{patientLabel}}'))
            {
              splitted[i] = splitted[i].replace('{{patientLabel}}', this.patientLabelString);
            }
          }
          value = splitted.toString();
          break;
        }
      case 'REQUIREDINSURANCEFIELDS':
        value = this.memberService.convertInsuranceFieldsToDisplayTexts(data.value);
        break;
      case 'DEFAULTGRIDFILTERPATIENT':
      case 'DEFAULTGRIDFILTERTASK':
      case 'DEFAULTGRIDFILTERCASE':
        value = this.textGridFilterValue;
        break;
      case 'SCHEDULERVIEWS':
        value = this.appointmentService.convertViewsToDisplayTexts(data.value);
        break;
      case 'PROVIDERTEAMROLEREFERRING':
      case 'PROVIDERTEAMROLEREFERREDTO':
        value = this.teamService.getTeamRoleDisplayText(this.teamRoles, data.value);
        break;
      case 'PATIENTCONTACTTEAMS':
        value = this.teamService.convertTeamRolesToDisplayTexts(this.teamRoles, data.value);
        break;
      case 'WORKFLOWRESTRICTEDUSERS':
      case 'REPORTRESTRICTEDUSERS':
      case 'EHRVIEWUSERS':
        value = this.userService.convertUsersToDisplayTexts(data.key.toUpperCase() === 'EHRVIEWUSERS' ?
          this.allUsers : this.programLeadsUsers, data.value);
        break;
      case 'PROVIDERDISTANCEDISPLAY':
        value = this.providerService.getDistanceDisplayText(data.value);
        break;
      case 'REPORTLOGODOCUMENTID':
      case 'BRANDLOGODOCUMENTID':
      case 'TEMPLATELOGODOCUMENTID':
      case 'HEADERLOGODOCUMENTID':
        value = data.documentName;
        break;
      case 'SYSTEMLANGUAGE':
        value = this.getLanguageText(data.value);
        break;
      case 'COMMUNITYASSESSMENTIDS':
      case 'COMMUNITYSELFSURVEYASSESSMENT':
        value = this.getAssessmentDisplayTexts(data.value);
        break;
      case 'CONSENTMODEL':
        value = this.consentService.getConsentModelDisplayText(data.value);
        break;
      case 'COMMUNITYDEFAULTLANDINGPAGE':
        value = this.settingService.convertCommunityDefaultLandingPageToDisplayTexts(data.value);
        break;
      case 'SUBJECTIDENTIFIERS':
        value = this.memberService.convertSubjectIdentifiersToDisplayTexts(this.subjectIdentifiers, data.value);
        break;
      case 'DEFAULTRESOURCESTATUSES':
        value = this.memberService.convertResourceStatusesToDisplayTexts(this.resourceStatuses, data.value);
        break;
      case 'CONSENTABLEOPTIONSDISABLEDREADONLYONINSERT':
        value = this.memberService.convertResourceStatusesToDisplayTexts(this.consentTypes, data.value);
        break;
      case 'GOALDICTIONARY':
        value = this.memberService.convertCodeSetsToDisplayTexts(this.codeSets, data.value);
        break;
      case 'PAYERBILLINGIDENTIFIER':
        value = this.settingService.convertPayerBillingIdentifierToDisplayTexts(this.communityServiceIdentifierTypes, data.value);
        break;
      case 'SENDINVITEDEFAULTEMAILTEMPLATE':
        {
          value = data.value;
          const templateObject = this.sendInviteTemplates.find(x => x.id === parseInt(data.value, 10));
          if (templateObject !== undefined)
          {
            value = templateObject.name;
          }
        }
        break;
      case 'DEFAULTCHANNELFORREFERRAL':
      case 'COMMUNITYSELFSURVEYASSESSMENTCHANNEL':
        {
          value = data.value;
          const channelObject = this.channels.find(x => x.id === parseInt(data.value, 10));
          if (channelObject !== undefined)
          {
            value = channelObject.name;
          }
          else
          {
            if (value !== '')
            {
              this.channelService.getMinimalChannelById(parseInt(data.value, 10)).subscribe({
                next: (lookupChannel) =>
                {
                  if (lookupChannel.length === 1)
                  {
                    lookupChannel.forEach(d =>
                    {
                      d.programName = d.program.name;
                      data.value = d.name;
                    });

                    // Add Channel to allChannels array to show but will not be able to select (will be disabled)
                    this.channels.push(lookupChannel[0]);
                  }
                  else
                  {
                    // Skip the load since we can't retrieve the channelId.
                    this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
                  }
                }
              });
            }
          }
          break;
        }
      case 'CONFIGURABLEDASHBOARD':
        value = this.settingService.convertConfigurableDashboardToDisplayTexts(data.value);
        break;
    }
    return value;
  }

  /**
   * Get setting name to display based on setting key
   *
   * @param key Setting key.
   */
  private getSettingName(key: string): string
  {
    const textObj = this.settingKeyTexts.find(x => x.key === key.toUpperCase());
    if (textObj !== undefined)
    {
      return textObj.text;
    }
    return key;
  }

  /**
   * Indicate if the setting is allowed per organization.
   *
   * @param key Setting key.
   * @returns boolean
   */
  private isPerOrganizationSetting(key: string): boolean
  {
    let returnValue = false;
    switch (key.toUpperCase())
    {
      case 'REGISTRATIONTOKENEXPIRATIONHOURS':
      case 'FORGOTPASSWORDTOKENEXPIRATIONHOURS':
      case 'EMAILCONTENTTYPE':
      case 'MAXLOGINATTEMPT':
      case 'SUBJECTIDENTIFIERS':
        returnValue = true;
        break;
    }
    return returnValue;
  }
}
